// Port seam for the dictation domain's ANALYTICS EMIT (ruling 27's
// in-package-analytics-is-a-port): the app's `track()` reaction stays app-side,
// so `src/utils/dictation-analytics-wiring.ts` injects the typed emitters at
// bootstrap. Same shape as the bridge port seam beside this file — fail-loud
// getter, module-held reference, test reset — held at the package root because
// the consumers span every ui area.
//
// The bundle is per-surface `*Ports` types from `/contracts`, each carrying the
// typed `Track*` signatures for events that surface already emits. Routing is
// decided ENTIRELY at the app binding (which options each host passes today is
// preserved byte-for-byte); nothing here may default, wrap, or normalize an
// emitter — the wiring test pins every member by identity for that reason.

import type {
  ButtonAction,
  FieldType,
  ScreenName,
} from '@heidi/runtime/analytics-vocabulary';
import type { DictationCleanupModes } from '@heidi/schema/ml-scribe';
import type {
  DictationCsatAnalyticsPorts,
  DictationFeedbackAnalyticsPorts,
  DictationLogsAnalyticsPorts,
  DictationOnboardingAnalyticsPorts,
  DictationReengagementAnalyticsPorts,
  DictationSessionAnalyticsPorts,
} from './contracts';

/**
 * UI-interaction emitters, typed HERE rather than in `/contracts` because the
 * signatures name the vocabulary package's enums, and the contracts door is
 * relative-import-only. This is the injected-config seam, which may reach a
 * runtime package — the same footing `flags.ts` stands on. The app binding
 * supplies the REAL wrappers by reference: `trackButtonClicked` is the helper
 * that enforces the button_clicked routing (Snowplow + Datadog), and
 * `trackFieldSelected` is the app wrapper whose options the call sites own.
 */
export interface DictationUiTrackingPorts {
  trackButtonClicked: (props: {
    screen_name: ScreenName;
    step_name?: string;
    action: ButtonAction;
    label: string;
    // Event-specific extras ride through to the app's `ButtonEventProps`.
    [extra: string]: unknown;
  }) => void;
  trackFieldSelected: (input: {
    fieldName: string;
    fieldType: FieldType;
    screenName: ScreenName;
    stepName?: string;
  }) => void;
  /**
   * The app's `TrackedButton` emit: `button_clicked` with the action and the
   * button's visible label, no screen name — a different payload from
   * `trackButtonClicked`, so it is its own member.
   */
  trackUnscopedButtonClicked: (action: ButtonAction, label: string) => void;
}

export interface DictationSettingsAnalyticsPorts {
  trackPageView: () => void;
  trackShortcutToggled: (isEnabled: boolean) => void;
  trackPlaybackToggled: (isEnabled: boolean) => void;
  trackSoundToggled: (isEnabled: boolean) => void;
  trackCaptureSilenceChimeToggled: (isEnabled: boolean) => void;
  trackCleanupModeChanged: (selectedValue: DictationCleanupModes) => void;
  trackHotkeySave: () => void;
}

/** One member per analytics surface the deferred dictation cone emits on. */
export interface DictationAnalyticsPorts {
  /** `universal_dictation_started/completed/errored` — the session funnel. */
  session: DictationSessionAnalyticsPorts;
  /** `survey_viewed` token + `universal_dictation_csat_submitted/dismissed`. */
  csat: DictationCsatAnalyticsPorts;
  /** Saved post-dictation thumbs feedback. */
  feedback: DictationFeedbackAnalyticsPorts;
  /** The desktop dictate onboarding funnel (`onboarding_*`). */
  onboarding: DictationOnboardingAnalyticsPorts;
  /** UI-interaction emitters (`button_clicked`, `field_selected`). */
  ui: DictationUiTrackingPorts;
  /** The Dictate-history surfaces (`/dictation-logs` and its tabs). */
  logs: DictationLogsAnalyticsPorts;
  /** The dictate re-engagement hints and general card. */
  reengagement: DictationReengagementAnalyticsPorts;
  /** The Dictation settings pane's existing Amplitude-routed interaction events. */
  settings: DictationSettingsAnalyticsPorts;
}

let ports: DictationAnalyticsPorts | null = null;

export const configureDictationAnalyticsPorts = (next: DictationAnalyticsPorts): void => {
  ports = next;
};

export const getDictationAnalyticsPorts = (): DictationAnalyticsPorts => {
  if (!ports) {
    throw new Error(
      '[dictation/analytics-ports] ports not configured — the app must load ' +
        "'@/utils/dictation-analytics-wiring' at bootstrap; tests must call " +
        'configureDictationAnalyticsPorts() with a double first.'
    );
  }
  return ports;
};

export const resetDictationAnalyticsPortsForTests = (): void => {
  ports = null;
};
